/**
 * Pure adapters that convert chart-tool wire shapes (matching the LLM-emitted
 * Zod schemas in `packages/workflow-apps/src/apps/ui/shared/chart_schemas.ts`)
 * into the legacy ui-blocks chart-wrapper prop shape.
 *
 * Kept pure (no React) so:
 *  - the chat-path React renderer (`chart-block-renderer.tsx`) can memoize calls,
 *  - the experiments slot can reuse `flattenAxisData` + `buildConfig`,
 *  - `chart-wire-adapters.test.ts` exercises edge cases without mounting React.
 */

import type { ChartConfig } from '@/types/ui-blocks';
import type {
  AreaChartWireContent,
  BarChartWireContent,
  ChartDataPoint,
  ChartSeriesBase,
  ChartSlice,
  LineChartWireContent,
  PieChartWireContent,
  RadialChartWireContent,
  ScatterChartWireContent,
} from './chart-wire-types';

export const X_AXIS_KEY = 'x';

// ---------------------------------------------------------------------------
// Primitive flatteners (also reused by experiment-detail-slot)
// ---------------------------------------------------------------------------

/**
 * Flatten axis-based wire data `[{x, values: [{series, value}]}]` into the
 * dynamic-key shape Recharts (and the ui-blocks wrappers above it) expect:
 * `[{x, [series]: value, ...}]`.
 */
export function flattenAxisData(data: ChartDataPoint[]): Record<string, unknown>[] {
  return data.map((point) => {
    const flat: Record<string, unknown> = { [X_AXIS_KEY]: point.x };
    for (const v of point.values) {
      flat[v.series] = v.value;
    }
    return flat;
  });
}

/**
 * Build the ui-blocks `ChartConfig` from a wire-shape series array. Missing
 * `color` lets the wrapper fall back to the default palette; `label` defaults
 * to the series `name` so legends stay informative even when the LLM omits it.
 */
export function buildConfig(series: ChartSeriesBase[]): ChartConfig {
  return series.reduce<ChartConfig>((acc, s) => {
    acc[s.name] = {
      label: s.label ?? s.name,
      ...(s.color ? { color: s.color } : {})
    };
    return acc;
  }, {});
}

/** Flatten slice wire data `[{label, value, color?}]` into ui-blocks slice rows. */
export function flattenSliceData(data: ChartSlice[]): Record<string, unknown>[] {
  return data.map((slice) => ({
    label: slice.label,
    value: slice.value,
    ...(slice.color ? { fill: slice.color } : {})
  }));
}

// ---------------------------------------------------------------------------
// Per-chart prop adapters
//
// Each returns the props for the matching ui-blocks chart wrapper, MINUS the
// wrapper-owned `title` / `description` / `className` (the per-renderer
// component layers those on so `TabbedChart` can strip them via
// `OmitWrapperProps`). `referenceLines/Dots/Areas` are coalesced from
// `null` to `undefined` to satisfy the wrapper's optional prop types.
// ---------------------------------------------------------------------------

function pickReferenceProps(content: {
  referenceLines: LineChartWireContent['referenceLines'];
  referenceDots: LineChartWireContent['referenceDots'];
  referenceAreas: LineChartWireContent['referenceAreas'];
}) {
  return {
    referenceLines: content.referenceLines ?? undefined,
    referenceDots: content.referenceDots ?? undefined,
    referenceAreas: content.referenceAreas ?? undefined
  };
}

export function adaptLineWireToProps(content: LineChartWireContent) {
  const data = flattenAxisData(content.data);
  const config = buildConfig(content.series);
  const lines = content.series.map((s) => ({
    dataKey: s.name,
    stroke: s.color ?? undefined,
    strokeWidth: s.strokeWidth ?? undefined,
    dot: s.dot ?? undefined,
    type: s.type ?? undefined,
    yAxisId: s.yAxisId ?? undefined
  }));
  return {
    data,
    xAxisKey: X_AXIS_KEY,
    config,
    lines,
    showGrid: content.showGrid ?? undefined,
    showTooltip: content.showTooltip ?? undefined,
    showLegend: content.showLegend ?? undefined,
    height: content.height ?? undefined,
    ...pickReferenceProps(content)
  };
}

export function adaptBarWireToProps(content: BarChartWireContent) {
  // BarChart accepts either `dataKeys` (high-level: key + label + color) or
  // `bars` (low-level Recharts overrides). We use `dataKeys` because the wire
  // shape's per-series styling stops at color/label — bars-level controls
  // (radius, custom fill function) aren't part of the schema and would never
  // arrive from the LLM.
  const data = flattenAxisData(content.data) as Record<string, string | number>[];
  const dataKeys = content.series.map((s) => ({
    key: s.name,
    name: s.label ?? s.name,
    color: s.color ?? undefined
  }));
  return {
    data,
    xAxisKey: X_AXIS_KEY,
    dataKeys,
    height: content.height ?? undefined,
    showGrid: content.showGrid ?? true,
    showLegend: content.showLegend ?? true,
    className: content.className ?? '',
    ...pickReferenceProps(content)
  };
}

export function adaptAreaWireToProps(content: AreaChartWireContent) {
  const data = flattenAxisData(content.data);
  const config = buildConfig(content.series);
  const areas = content.series.map((s) => ({
    dataKey: s.name,
    type: s.type ?? undefined,
    fill: s.color ?? undefined,
    fillOpacity: s.fillOpacity ?? undefined,
    stackId: s.stackId ?? undefined
  }));
  return {
    data,
    xAxisKey: X_AXIS_KEY,
    config,
    areas,
    showGrid: content.showGrid ?? undefined,
    showTooltip: content.showTooltip ?? undefined,
    showLegend: content.showLegend ?? undefined,
    height: content.height ?? undefined,
    ...pickReferenceProps(content)
  };
}

export function adaptPieWireToProps(content: PieChartWireContent) {
  return {
    data: flattenSliceData(content.data),
    dataKey: 'value' as const,
    nameKey: 'label' as const,
    showTooltip: content.showTooltip ?? undefined,
    showLegend: content.showLegend ?? undefined,
    height: content.height ?? undefined,
    innerRadius: content.innerRadius ?? undefined,
    outerRadius: content.outerRadius ?? undefined,
    paddingAngle: content.paddingAngle ?? undefined
  };
}

export function adaptRadialWireToProps(content: RadialChartWireContent) {
  return {
    data: flattenSliceData(content.data),
    dataKey: 'value' as const,
    nameKey: 'label' as const,
    showTooltip: content.showTooltip ?? undefined,
    showLegend: content.showLegend ?? undefined,
    height: content.height ?? undefined,
    innerRadius: content.innerRadius ?? undefined,
    outerRadius: content.outerRadius ?? undefined,
    startAngle: content.startAngle ?? undefined,
    endAngle: content.endAngle ?? undefined
  };
}

export function adaptScatterWireToProps(content: ScatterChartWireContent) {
  // For scatter, several observations can land at the same `x` for the same
  // series — use `filter` (not `find`) so duplicates per point survive into
  // the rendered cloud. `data` rows with the same `x` are also handled by the
  // outer `flatMap`, so the adapter captures both within-point and
  // across-points duplicates.
  const scatters = content.series.map((s) => {
    const seriesPoints = content.data.flatMap((point) => {
      return point.values
        .filter((v) => v.series === s.name && v.value !== null)
        .map((v) => ({ x: point.x, y: v.value as number }));
    });
    return {
      name: s.label ?? s.name,
      data: seriesPoints,
      fill: s.color ?? undefined,
      shape: s.shape ?? undefined
    };
  });
  const config = buildConfig(content.series);
  return {
    data: [],
    xAxisKey: 'x' as const,
    yAxisKey: 'y' as const,
    scatters,
    config,
    showGrid: content.showGrid ?? undefined,
    showTooltip: content.showTooltip ?? undefined,
    showLegend: content.showLegend ?? undefined,
    height: content.height ?? undefined,
    xAxisLabel: content.xAxisLabel ?? undefined,
    yAxisLabel: content.yAxisLabel ?? undefined
  };
}
